/**
 * SystemProductAddSearch — the "+ Add product" affordance on the Brand &
 * product preferences surface (T12.2 step 8).
 *
 * The household-wide system-product like/avoid lists need a search-first add
 * control: the SystemProduct catalog is too large to fetch wholesale (unlike
 * the brand list, which `InlineAddSearch` filters in memory). This mirrors the
 * per-product `SystemProductPicker` search — a `.setsearch` row backed by the
 * server `useSearchSystemProducts` query — but collapses the picker's
 * preferred/substitute/exclude actions into one `onPick(id)` callback (the
 * caller decides like vs avoid by which list the control belongs to).
 *
 * Already-linked rows render a muted "added" badge instead of an Add button.
 * Styles flow from tokens (CP5): no hex, no raw px.
 */

import { useMemo, useState } from 'react';
import { useSearchSystemProducts } from '@shopista/shared/api/hooks';
import type { SystemProductSearchParams } from '@shopista/shared/types';
import { Spinner } from '../../components';
import {
  tagAddStyle,
  setsearchStyle,
  setsearchInputStyle,
  presultboxStyle,
  presultStyle,
  presultMetaStyle,
  presultNmStyle,
  miniaddSubStyle,
  connectedBadgeStyle,
  mutedStyle,
  errorStyle,
} from '../products/settingsStyles';
import { ProductSearchReporter } from '../report-problems';

export function SystemProductAddSearch({
  label,
  linkedIds,
  onPick,
  testId,
}: {
  /** Chip text, e.g. "+ Add product". */
  label: string;
  /** SystemProduct ids already on either list (rendered as "added", not addable). */
  linkedIds: Set<string>;
  onPick: (systemProductId: string) => void;
  /** data-testid stem; the chip is `{testId}-add`, the input `{testId}-input`. */
  testId: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');

  const params: SystemProductSearchParams = useMemo(
    () => ({ query: term.trim(), limit: 20, offset: 0 }),
    [term]
  );
  const searchQuery = useSearchSystemProducts(params, open && term.trim().length > 0);
  const results = searchQuery.data?.items ?? [];

  if (!open) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={tagAddStyle}
        onClick={() => setOpen(true)}
        data-testid={`${testId}-add`}
      >
        {label}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} data-testid={`${testId}-panel`}>
      <div className="field-focus-ring" style={setsearchStyle}>
        <span aria-hidden="true" style={{ color: 'var(--color-text-muted)' }}>
          🔍
        </span>
        <input
          style={setsearchInputStyle}
          aria-label={label}
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search products to add…"
          data-testid={`${testId}-input`}
        />
        <span
          role="button"
          tabIndex={0}
          aria-label="Cancel"
          style={{ ...mutedStyle, cursor: 'pointer' }}
          onClick={() => {
            setOpen(false);
            setTerm('');
          }}
          data-testid={`${testId}-cancel`}
        >
          ✕
        </span>
      </div>

      {term.trim().length === 0 ? (
        <span style={{ ...mutedStyle, marginTop: 'var(--space-2)' }} data-testid={`${testId}-hint`}>
          Type to search the product catalog.
        </span>
      ) : searchQuery.isLoading ? (
        <div data-testid={`${testId}-loading`}>
          <Spinner />
        </div>
      ) : searchQuery.isError ? (
        <span style={errorStyle} data-testid={`${testId}-error`}>
          Could not search products.
        </span>
      ) : results.length === 0 ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
          data-testid={`${testId}-empty`}
        >
          <span style={mutedStyle}>No matching products.</span>
          <ProductSearchReporter searchTerm={term} surface="household" />
        </div>
      ) : (
        <div style={presultboxStyle} data-testid={`${testId}-results`}>
          {results.map((r) => {
            const linked = linkedIds.has(r.id);
            return (
              <div style={presultStyle} key={r.id} data-testid={`${testId}-option-${r.id}`}>
                <div style={presultMetaStyle}>
                  <div style={presultNmStyle}>{r.name}</div>
                </div>
                {linked ? (
                  <span style={connectedBadgeStyle} data-testid={`${testId}-option-${r.id}-added`}>
                    added
                  </span>
                ) : (
                  <button
                    type="button"
                    style={miniaddSubStyle}
                    onClick={() => {
                      onPick(r.id);
                      setOpen(false);
                      setTerm('');
                    }}
                    data-testid={`${testId}-pick-${r.id}`}
                  >
                    Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
